import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { FieldStatus } from './card-batch';

export function FieldsPreview({
  statuses,
  csvColumns,
}: {
  statuses: FieldStatus[];
  csvColumns: string[];
}) {
  const { t } = useTranslation();
  const missing = statuses.filter((f) => f.source === 'missing');
  const ok = statuses.filter((f) => f.source !== 'missing');
  const unusedCsvCols = csvColumns.filter(
    (c) => !statuses.find((f) => f.name === c),
  );
  const hasMissing = missing.length > 0;
  const [userExpanded, setUserExpanded] = useState(false);
  // Auto-expand whenever there are missing fields; user can still collapse
  // when nothing is missing. Derived state — no effect needed.
  const expanded = hasMissing || userExpanded;
  const setExpanded = (next: boolean) => setUserExpanded(next);

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="flex w-full items-center justify-between border border-green/40 bg-green/10 px-3 py-2 text-sm text-green-ink transition-colors"
      >
        <span className="flex items-center gap-2">
          <svg
            className="size-4 shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
          {t('cardGenerator.fieldsMapping.mappedFields', { count: ok.length })}
          {unusedCsvCols.length > 0 && (
            <span className="text-xs text-muted-soft">
              ·{' '}
              {t('cardGenerator.fieldsMapping.unusedColsCount', {
                count: unusedCsvCols.length,
              })}
            </span>
          )}
        </span>
        <svg
          className="size-3.5 text-green-ink"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
    );
  }

  return (
    <div className="overflow-hidden border border-rule text-sm">
      <div className="flex items-center justify-between bg-ink/3 px-3 py-2 text-xs font-medium text-ink-soft">
        <span className="tracking-wide uppercase">
          {t('cardGenerator.fieldsMapping.title')}
        </span>
        {!hasMissing && (
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="tracking-normal text-muted-soft normal-case"
          >
            {t('cardGenerator.fieldsMapping.collapse')}
          </button>
        )}
      </div>
      <div>
        {ok.map((f) => (
          <div
            key={f.name}
            className="flex items-center justify-between border-t border-t-rule px-3 py-2"
          >
            <span className="font-mono text-ink">
              {'{{ '}
              {f.name}
              {' }}'}
            </span>
            <span
              className="px-2 py-0.5 text-xs font-medium"
              style={
                f.source === 'csv'
                  ? {
                      background: 'rgba(0,255,151,0.18)',
                      color: '#007a47',
                    }
                  : f.source === 'default'
                    ? {
                        background: 'rgba(24,24,24,0.05)',
                        color: 'var(--muted)',
                      }
                    : {
                        background: 'rgba(1,58,251,0.10)',
                        color: 'var(--blue)',
                      }
              }
            >
              {f.source === 'csv'
                ? t('cardGenerator.fieldsMapping.sourceCSV')
                : f.source === 'default'
                  ? t('cardGenerator.fieldsMapping.sourceDefault')
                  : t('cardGenerator.fieldsMapping.sourceAuto')}
            </span>
          </div>
        ))}
        {missing.map((f) => (
          <div
            key={f.name}
            className="flex items-center justify-between border-t border-t-rule bg-magenta/6 px-3 py-2"
          >
            <span className="font-mono text-magenta">
              {'{{ '}
              {f.name}
              {' }}'}
            </span>
            <span className="bg-magenta/10 px-2 py-0.5 text-xs font-medium text-magenta">
              {t('cardGenerator.fieldsMapping.sourceMissing')}
            </span>
          </div>
        ))}
        {unusedCsvCols.length > 0 && (
          <div className="border-t border-t-rule px-3 py-2 text-xs text-muted-soft">
            {t('cardGenerator.fieldsMapping.unusedCols', {
              cols: unusedCsvCols.join(', '),
            })}
          </div>
        )}
        {statuses.length === 0 && (
          <div className="px-3 py-2 text-xs text-muted-soft">
            {t('cardGenerator.fieldsMapping.noVars')}
          </div>
        )}
      </div>
    </div>
  );
}
